import {
    useState,
    useEffect,
    ChangeEventHandler,
    KeyboardEventHandler,
} from 'react';
import { checkPassword } from '@utils/dataCheck';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import { IState } from './IState';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';

const usePasswordConfirm = (onClose: ICloseEvent['onClose']) => {
    const { user, lang } = useStore();
    const key = useKeyDown();

    const initState: IState = {
        password: '',
        passwordConfirm: '',
        code: '',
        passwordError: false,
        passwordConfirmError: false,
        codeError: false,
    };
    const [state, setState] = useState(initState);

    const passwordValidate = (password: string) => {
        const isPasswordValid = checkPassword(password);
        if (!isPasswordValid) {
            setState((prev) => ({
                ...prev,
                passwordError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                passwordError: false,
            }));
            return true;
        }
    };
    const passwordConfirmValidate = (password: string, confirm: string) => {
        if (password !== confirm) {
            setState((prev) => ({
                ...prev,
                passwordConfirmError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                passwordConfirmError: false,
            }));
            return true;
        }
    };
    const codeValidate = (code: string) => {
        if (!code) {
            setState((prev) => ({
                ...prev,
                codeError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                codeError: false,
            }));
            return true;
        }
    };
    const handleInputClick = () => {
        user.resetConfirmError();
        user.resetSendAlert();
        user.resetServerError();
        setState((prev) => ({
            ...prev,
            codeError: false,
            passwordError: false,
        }));
    };
    const handlePasswordChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const password = e.target.value;
        setState((prev) => ({
            ...prev,
            password,
            passwordError: false,
            passwordConfirmError: false,
        }));
    };
    const handlePasswordConfirmChange: ChangeEventHandler<HTMLInputElement> = (
        e,
    ) => {
        handleInputClick();
        const passwordConfirm = e.target.value;
        setState((prev) => ({
            ...prev,
            passwordConfirm,
            passwordError: false,
            passwordConfirmError: false,
        }));
    };
    const handleCodeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const code = e.target.value;
        setState((prev) => ({
            ...prev,
            code,
        }));
    };
    const handleConfirmClick = () => {
        handleInputClick();
        const { email } = user;
        const { password, passwordConfirm, code } = state;
        const isCodeValid = codeValidate(code);
        const isPasswordValid = passwordValidate(password);
        const isConfirmValid = passwordConfirmValidate(
            password,
            passwordConfirm,
        );
        if (email && isCodeValid && isPasswordValid && isConfirmValid) {
            user.confirmPasswordRecovery(email, password, code);
            setState((prev) => ({
                ...prev,
                code: '',
            }));
        }
    };
    const handleRequestConfirmClick = () => {
        handleInputClick();
        const { email } = user;
        if (email) {
            user.passwordRecovery(email);
        }
    };
    const handleBackClick = () => {
        user.resetConfirmed();
        user.resetPasswordConfirmed();
        user.resetEmail();
    };
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleConfirmClick();
                break;
        }
    };
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            handleKey(key);
        }
    };
    const handleBtnKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            const target = e.target as HTMLElement;
            target.click();
        }
    };
    const handlers = {
        handlePasswordChange,
        handlePasswordConfirmChange,
        handleCodeChange,
        handleConfirmClick,
        handleRequestConfirmClick,
        handleInputClick,
        handleBackClick,
        handleKeyDown,
        handleBtnKeyDown,
    };

    useEffect(() => {
        handleInputClick();
        return () => {
            user.stopTimer();
            user.resetEmail();
            handleInputClick();
        };
    }, []);
    useEffect(() => {
        handleKey(key);
    }, [key]);

    return {
        store: user,
        lang,
        state,
        handlers,
    };
};

export default usePasswordConfirm;
